import { useState, type ReactNode } from "react";
import { Mail, MapPin, Phone, Send, MessageCircle } from "lucide-react";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import { contactInfo } from "../../../mocks/homeData";

export default function CTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const whatsappText = [
      "Merhaba Hangar Muhendislik,",
      `Ad Soyad: ${formData.name}`,
      `Telefon: ${formData.phone}`,
      `E-posta: ${formData.email}`,
      `Proje Detayi: ${formData.message}`,
    ].join("\n");

    const whatsappUrl = `https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(whatsappText)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");

    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ name: "", email: "", phone: "", message: "" });
    setTimeout(() => setIsSubmitted(false), 4000);
  };

  return (
    <section id="contact" className="relative bg-navy py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(196,122,44,.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(196,122,44,.2) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      <div ref={ref} className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className={`${isVisible ? "animate-slide-in-left" : "opacity-0"}`}>
            <div className="inline-block w-12 h-1 rounded-full bg-copper mb-6" />
            <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight mb-6">
              Projenizi Birlikte Gerceklestirelim
            </h2>
            <p className="font-body text-base md:text-lg text-white/75 leading-relaxed mb-8">
              Ucretsiz kesif ve teklif sureci icin bilgilerinizi paylasin. Ekipten size ayni gun icinde geri donus yapalim.
            </p>
            <div className="space-y-4">
              <ContactRow icon={<Phone size={18} />} label="Telefon" value={`${contactInfo.phone} / ${contactInfo.phoneSecondary}`} />
              <ContactRow icon={<Mail size={18} />} label="E-posta" value={contactInfo.email} />
              <ContactRow icon={<MapPin size={18} />} label="Adres" value={contactInfo.address} />
            </div>
          </div>

          <div className={`${isVisible ? "animate-slide-in-right" : "opacity-0"}`} style={{ animationDelay: "0.2s" }}>
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-xl border border-white/20">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <Send className="text-green-600" size={30} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-navy mb-2">Talebiniz Alindi</h3>
                  <p className="font-body text-sm text-gray-600">WhatsApp uzerinden teklif detaylariniz iletildi.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="font-heading font-bold text-xl text-navy mb-4">Teklif Alin</h3>
                  <input
                    type="text"
                    placeholder="Adiniz Soyadiniz"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full font-body text-sm px-4 py-3 rounded-md border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper/20 outline-none transition-all placeholder:text-gray-400"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="E-posta Adresiniz"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full font-body text-sm px-4 py-3 rounded-md border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper/20 outline-none transition-all placeholder:text-gray-400"
                  />
                  <input
                    type="tel"
                    placeholder="Telefon Numaraniz"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full font-body text-sm px-4 py-3 rounded-md border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper/20 outline-none transition-all placeholder:text-gray-400"
                  />
                  <textarea
                    placeholder="Projeniz hakkinda kisa bilgi..."
                    rows={4}
                    required
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full font-body text-sm px-4 py-3 rounded-md border border-gray-200 focus:border-copper focus:ring-1 focus:ring-copper/20 outline-none transition-all resize-none placeholder:text-gray-400"
                  />
                  <div className="flex items-center justify-between">
                    <p className="font-body text-xs text-gray-400">{formData.message.length}/500</p>
                    <a
                      href={`https://wa.me/${contactInfo.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-800"
                    >
                      <MessageCircle size={14} />
                      Direkt WhatsApp
                    </a>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-copper hover:bg-copper-dark text-white font-heading font-semibold text-base py-4 rounded-md transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Gonderiliyor..." : "Teklifi WhatsApp'tan Gonder"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 rounded-full bg-copper/20 flex items-center justify-center text-copper flex-shrink-0">
        {icon}
      </div>
      <div>
        <p className="font-body text-xs text-white/50 mb-0.5">{label}</p>
        <p className="font-body text-sm md:text-base text-white font-medium">{value}</p>
      </div>
    </div>
  );
}
